import { useState, useEffect, useMemo, memo } from 'react';
import { NavLink } from '../imports/generalImport';
import { Container, Skeleton, Box, useMediaQuery } from '../imports/muiImport';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import { getDummySliderData } from '../localData/dummyData';

export const SliderComponent = ({ height = 181, showPagination = true }) => {
  const [sliderData, setSliderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    const fetchSliderData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getDummySliderData();
        setSliderData(response?.data);
      } catch (err) {
        console.error('Failed to fetch slider data:', err);
        setError('Failed to load sliders');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSliderData();
  }, []);

  const filteredSliders = useMemo(() => {
    if (!sliderData || !Array.isArray(sliderData)) return [];
    return sliderData.filter((item) => item && (isMobile ? item?.mobileSliderImage : item?.image));
  }, [isMobile, sliderData]);

  const swiperParams = useMemo(
    () => ({
      modules: [Autoplay, Pagination],
      spaceBetween: 0,
      slidesPerView: 1,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      },
      speed: 500,
      pagination: showPagination
        ? {
            clickable: true,
            dynamicBullets: true,
            renderBullet: function (index, className) {
              return `<span class="${className}"></span>`;
            },
          }
        : false,
      touchThreshold: 15,
      resistance: true,
      resistanceRatio: 0.85,
      watchSlidesProgress: true,
      breakpoints: {
        600: {
          speed: 300,
          touchRatio: 0.6,
        },
      },
    }),
    [showPagination]
  );

  if (error) {
    return (
      <Container
        maxWidth="false"
        disableGutters
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: { xs: height, sm: '260px', md: '320px' },
          backgroundColor: 'grey.100',
          borderRadius: { xs: 2, sm: 0 },
          color: 'error.main',
          fontSize: '14px',
        }}
      >
        {error}
      </Container>
    );
  }

  return (
    <Container
      maxWidth="false"
      disableGutters
      sx={{
        pt: {
          xs: 3,
          md: 0,
        },
        position: 'relative',
        userSelect: 'none',
        touchAction: 'pan-y',
        '& .swiper-pagination': {
          bottom: '8px !important',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
        },
        // '& .swiper-pagination-bullet': {
        //   width: '12px',
        //   height: '12px',
        //   backgroundColor: 'transparent',
        //   border: '1px solid #002865',
        //   opacity: 1,
        //   transition: 'all 0.3s ease',
        // },
        '& .swiper-pagination-bullet-active': {
          backgroundColor: '#00B8EE',
        },
        // Ensure Swiper container has proper height for dots to be visible
        '& .swiper': {
          height: '100%',
        },
        '& .swiper-wrapper': {
          height: '100%',
        },
        '& .swiper-slide': {
          height: '100%',
        },
      }}
    >
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          sx={{
            height: {
              xs: height,
              sm: '260px',
              md: '320px',
            },
          }}
        />
      ) : (
        filteredSliders?.length > 0 && (
          <Swiper {...swiperParams}>
            {filteredSliders.map((item, index) => {
              const customURL = `${item?.url}${item?.url_utm ? `?${item?.url_utm}` : ''}`;
              const key = `hero-slider-${
                item?.id || item?.url || item?.mobileSliderImage || item?.image || index
              }`;

              const slideContent = <SliderImage item={item} height={height} isMobile={isMobile} />;

              return (
                <SwiperSlide key={key}>
                  {item?.url ? (
                    <NavLink to={customURL} style={{ display: 'block', height: '100%' }}>
                      {slideContent}
                    </NavLink>
                  ) : (
                    slideContent
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
        )
      )}
    </Container>
  );
};

const SliderImage = memo(({ item, height, isMobile }) => {
  const imageSrc = useMemo(
    () => (isMobile ? item?.mobileSliderImage : item?.image),
    [isMobile, item]
  );

  return (
    <Box
      component="img"
      src={imageSrc}
      alt={`${item?.title || 'slider'}-99gift-hero-sliders`}
      loading="lazy"
      sx={{
        width: { xs: '100%', md: '100vw' },
        height: {
          xs: height,
          sm: '260px',
          md: 'auto',
        },
        objectFit: {
          xs: 'contain',
          sm: 'contain',
        },
        objectPosition: 'center',
        borderRadius: { xs: 2, sm: 0 },
        backgroundColor: { xs: 'white.main', sm: 'backgroundPrimary.main' },
        userSelect: 'none',
        pointerEvents: 'none',
        display: 'block',
        mt: 3,
      }}
    />
  );
});
